import React, { Suspense } from "react";
import { createBrowserRouter, createRoutesFromElements, Route } from "react-router-dom";
import ProtectedRoute, { ProtectedRouteOptions } from "./ProtectedRoute";
import { JmfPermissionEntity, PermissionNameType } from "@mm-products/java-multima-framework";
import { TaxPermissionBuilder, TaxPermissionEntity } from "../permissions/TaxGuruPermissionBuilder";
import { dashboardPermission, navigationLinks, passwordResetLink, registrationLink } from "./Navigation";
import TaxGuru, { TaxLogoFallback } from "../TaxGuru";
import { SUPER_ADMIN_ROLE } from "../permissions/usePermissions";

const DmsPage = React.lazy(() => import("../modules/dms/DmsPage"));
const RolesPage = React.lazy(() => import("../modules/roles/RolesPage"));
const NewUserRolePage = React.lazy(() => import("../modules/roles/NewUserRolePage"));
const UserRoleDetailPage = React.lazy(() => import("../modules/roles/UserRoleDetailPage"));
const AccountSettingsTab = React.lazy(() => import("../modules/account/tabs/AccountSettingsTab"));
const AccountExportsTab = React.lazy(() => import("../modules/account/tabs/AccountExportsTab"));
const AccountPreferencesTab = React.lazy(() => import("../modules/account/tabs/AccountPreferencesTab"));
const PreAccountingPage = React.lazy(() => import("../modules/preaccounting/PreAccountingPage"));
const NewPreAccountingPage = React.lazy(() => import("../modules/preaccounting/NewPreAccountingPage"));
const PreAccountingDetailPage = React.lazy(() => import("../modules/preaccounting/PreAccountingDetailPage"));
const AddressesPage = React.lazy(() => import("../modules/address/AddressesPage"));
const NewAddressPage = React.lazy(() => import("../modules/address/NewAddressPage"));
const AddressDetailPage = React.lazy(() => import("../modules/address/AddressDetailPage"));
const DashboardPage = React.lazy(() => import("../modules/dashboard/DashboardPage"));
const AccountsPage = React.lazy(() => import("../modules/account/AccountPage"));
const NewAccountPage = React.lazy(() => import("../modules/account/NewAccountPage"));
const AccountDetailPage = React.lazy(() => import("../modules/account/AccountDetailPage"));
const SchedulerPage = React.lazy(() => import("../modules/scheduler/SchedulerPage"));
const NewSchedulerJobPage = React.lazy(() => import("../modules/scheduler/NewSchedulerJobPage"));
const SchedulerJobDetailPage = React.lazy(() => import("../modules/scheduler/SchedulerJobDetailPage"));
const TaskQueuePage = React.lazy(() => import("../modules/tasks/TaskQueuePage"));
const TaskQueueDetailPage = React.lazy(() => import("../modules/tasks/TaskQueueDetailPage"));
const DocumentsPage = React.lazy(() => import("../modules/document/DocumentsPage"));
const DocumentDetailPage = React.lazy(() => import("../modules/document/DocumentDetailPage"));
const NewAccountantPage = React.lazy(() => import("../modules/accountant/NewAccountantPage"));
const AccountantDetailPage = React.lazy(() => import("../modules/accountant/AccountantDetailPage"));
const AccountantsPage = React.lazy(() => import("../modules/accountant/AccountantsPage"));
const ClientsPage = React.lazy(() => import("../modules/clients/ClientsPage"));
const NewClientPage = React.lazy(() => import("../modules/clients/NewClientPage"));
const ClientDetailPage = React.lazy(() => import("../modules/clients/ClientDetailPage"));
const LicencesPage = React.lazy(() => import("../modules/licence/LicencesPage"));
const NewLicencePage = React.lazy(() => import("../modules/licence/NewLicencePage"));
const LicenceDetailPage = React.lazy(() => import("../modules/licence/LicenceDetailPage"));
const CompaniesPage = React.lazy(() => import("../modules/company/CompanyPage"));
const NewCompanyPage = React.lazy(() => import("../modules/company/NewCompanyPage"));
const CompanyDetailPage = React.lazy(() => import("../modules/company/CompanyDetailPage"));
const PersonsPage = React.lazy(() => import("../modules/person/PersonsPage"));
const NewPersonPage = React.lazy(() => import("../modules/person/NewPersonPage"));
const PersonDetailPage = React.lazy(() => import("../modules/person/PersonDetailPage"));
const AuditPage = React.lazy(() => import("../modules/audit/AuditPage"));
const AuditDetailPage = React.lazy(() => import("../modules/audit/AuditDetailPage"));
const PaymentRequestsPage = React.lazy(() => import("../modules/paymentrequest/PaymentRequestsPage"));
const PaymentRequestDetailPage = React.lazy(() => import("../modules/paymentrequest/PaymentRequestDetailPage"));
const PaymentTransactionPage = React.lazy(() => import("../modules/paymenttransaction/PaymentTransactionPage"));
const ContactPage = React.lazy(() => import("../modules/contact/ContactPage"));
const NewContactPage = React.lazy(() => import("../modules/contact/NewContactPage"));
const ContactDetailPage = React.lazy(() => import("../modules/contact/ContactDetailPage"));
const ResetPasswordPage = React.lazy(() => import("./passwordreset/ResetPasswordPage"));
const PaymentOrdersPage = React.lazy(() => import("../modules/paymentorder/PaymentOrdersPage"));
const PaymentOrderDetailPage = React.lazy(() => import("../modules/paymentorder/PaymentOrderDetailPage"));
const PaymentOfferPage = React.lazy(() => import("../modules/paymentoffer/PaymentOfferPage"));
const PaymentOfferDetailPage = React.lazy(() => import("../modules/paymentoffer/PaymentOfferDetailPage"));
const NewPaymentOfferPage = React.lazy(() => import("../modules/paymentoffer/NewPaymentOfferPage"));
const PaymentCouponPage = React.lazy(() => import("../modules/paymentcoupon/PaymentCouponPage"));
const PaymentCouponDetailPage = React.lazy(() => import("../modules/paymentcoupon/PaymentCouponDetailPage"));
const NewPaymentCouponPage = React.lazy(() => import("../modules/paymentcoupon/NewPaymentCouponPage"));
const AboutPage = React.lazy(() => import("../navigation/about/AboutPage"));
const FeedbackPage = React.lazy(() => import("../modules/feedback/FeedbackPage"));
const OnboardingFeedbackPage = React.lazy(() => import("../modules/feedback/OnboardingFeedbackPage"));
const RegistrationPage = React.lazy(() => import("../modules/registration/selfregistration/SelfRegistrationPage"));
const RegistrationRequestsPage = React.lazy(() => import("../modules/registration/agenda/RegistrationRequestsPage"));
const RegistrationRequestDetailPage = React.lazy(() => import("../modules/registration/agenda/RegistrationRequestDetailPage"));
const VatRatesPage = React.lazy(() => import("../modules/vatRates/VatRatesPage"));
const NewVatRatesPage = React.lazy(() => import("../modules/vatRates/NewVatRatePage"));
const VatRatesDetailPage = React.lazy(() => import("../modules/vatRates/VatRatesDetailPage"));
const EmailsPage = React.lazy(() => import("../modules/email/EmailsPage"));
const ActivitiesPage = React.lazy(() => import("../modules/activity/ActivitiesPage"));
const CentrePage = React.lazy(() => import("../modules/centre/CentrePage"));
const CentreDetailPage = React.lazy(() => import("../modules/centre/CentreDetailPage"));
const NewCentrePage = React.lazy(() => import("../modules/centre/NewCentrePage"));
const EmailDetailPage = React.lazy(() => import("../modules/email/EmailDetailPage"));
const NewActivityPage = React.lazy(() => import("../modules/activity/NewActivityPage"));
const ContractPage = React.lazy(() => import("../modules/contract/ContractPage"));
const ContractDetailPage = React.lazy(() => import("../modules/contract/ContractDetailPage"));
const NewContractPage = React.lazy(() => import("../modules/contract/NewContractPage"));
const ActivityDetailPage = React.lazy(() => import("../modules/activity/ActivityDetailPage"));
const OfficersPage = React.lazy(() => import("../modules/officer/OfficersPage"));
const NewOfficerPage = React.lazy(() => import("../modules/officer/NewOfficerPage"));
const OfficersDetailPage = React.lazy(() => import("../modules/officer/OfficerDetailPage"));
const ApproversPage = React.lazy(() => import("../modules/approver/ApproversPage"));
const NewApproverPage = React.lazy(() => import("../modules/approver/NewApproverPage"));
const ApproversDetailPage = React.lazy(() => import("../modules/approver/ApproverDetailPage"));
const SubscriptionDashboardPage = React.lazy(() => import("../modules/subscription/SubscriptionDashboardPage"));

export const protectedRoute = (permissions: Array<PermissionNameType>, children: React.ReactNode, options?: ProtectedRouteOptions) => (
    <ProtectedRoute permissions={permissions} options={options}>
        {children}
    </ProtectedRoute>
);

export const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route path={passwordResetLink} element={<ResetPasswordPage />} />
            <Route
                path={registrationLink}
                element={
                    <Suspense fallback={<TaxLogoFallback />}>
                        <RegistrationPage />
                    </Suspense>
                }
            />
            <Route element={<TaxGuru />}>
                <Route
                    path="/"
                    element={protectedRoute(dashboardPermission, <DashboardPage />, {
                        linksOnFail: [
                            {
                                link: navigationLinks.TAX_DOCUMENTS_LINK,
                                permissions: TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_DOCUMENT).build(),
                            },
                            {
                                link: navigationLinks.TAX_ACCOUNTANTS_LINK,
                                permissions: TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_ACCOUNTANT).build(),
                            },
                        ],
                        noErrorToast: true,
                    })}
                />
                {/* --- C O R E --- */}
                {/* --- USERS --- */}
                <Route
                    path={navigationLinks.ADMIN_USERS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_USER).build(), <AccountsPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_USERS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(JmfPermissionEntity.CORE_USER).build(), <NewAccountPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_USERS_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_USER).build(), <AccountDetailPage />)}
                >
                    <Route index element={<AccountSettingsTab />} />
                    <Route path="settings" element={<AccountSettingsTab />} />
                    <Route path="exports" element={<AccountExportsTab />} />
                    <Route path="preferences" element={<AccountPreferencesTab />} />
                </Route>
                {/* --- SCHEDULERS --- */}
                <Route
                    path={navigationLinks.ADMIN_SCHEDULERS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_SCHEDULER).build(), <SchedulerPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_SCHEDULERS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_SCHEDULER).build(), <NewSchedulerJobPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_SCHEDULERS_LINK}/:id`}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_SCHEDULER).build(),
                        <SchedulerJobDetailPage />
                    )}
                />
                {/* --- TASKS --- */}
                <Route
                    path={navigationLinks.ADMIN_TASKS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_TASK_QUEUE).build(), <TaskQueuePage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_TASKS_LINK}/:id`}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_TASK_QUEUE).build(),
                        <TaskQueueDetailPage />
                    )}
                />
                {/* --- ATTACHMENTS --- */}
                <Route
                    path={navigationLinks.ADMIN_ATTACHMENTS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_ATTACHMENT).build(), <DmsPage />)}
                />
                {/* --- ROLES --- */}
                <Route
                    path={navigationLinks.ADMIN_ROLES_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_ROLE).build(), <RolesPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_ROLES_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(JmfPermissionEntity.CORE_ROLE).build(), <NewUserRolePage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_ROLES_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_ROLE).build(), <UserRoleDetailPage />)}
                />
                {/* --- COMPANIES --- */}
                <Route
                    path={navigationLinks.TAX_COMPANIES_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_COMPANY).build(), <CompaniesPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_COMPANIES_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(JmfPermissionEntity.CORE_COMPANY).build(), <NewCompanyPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_COMPANIES_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_COMPANY).build(), <CompanyDetailPage />)}
                />
                {/* --- PERSONS --- */}
                <Route
                    path={navigationLinks.ADMIN_PERSONS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PERSON).build(), <PersonsPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_PERSONS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(JmfPermissionEntity.CORE_PERSON).build(), <NewPersonPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_PERSONS_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PERSON).build(), <PersonDetailPage />)}
                />
                {/* --- ADDRESSES --- */}
                <Route
                    path={navigationLinks.ADMIN_ADDRESSES_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_ADDRESS).build(), <AddressesPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_ADDRESSES_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(JmfPermissionEntity.CORE_ADDRESS).build(), <NewAddressPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_ADDRESSES_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_ADDRESS).build(), <AddressDetailPage />)}
                />
                {/* --- T A X --- */}
                {/* --- DOCUMENT --- */}
                <Route
                    path={navigationLinks.TAX_DOCUMENTS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_DOCUMENT).build(), <DocumentsPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_DOCUMENTS_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_DOCUMENT).build(), <DocumentDetailPage />)}
                />
                {/* --- ACCOUNTANTS --- */}
                <Route
                    path={navigationLinks.TAX_ACCOUNTANTS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_ACCOUNTANT).build(), <AccountantsPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_ACCOUNTANTS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_ACCOUNTANT).build(), <NewAccountantPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_ACCOUNTANTS_LINK}/:id`}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_ACCOUNTANT).build(),
                        <AccountantDetailPage />
                    )}
                />
                {/* --- PREACCOUNTING --- */}
                <Route
                    path={navigationLinks.TAX_PREACCOUNTING_LINK}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_PREACCOUNTING).build(),
                        <PreAccountingPage />
                    )}
                />
                <Route
                    path={`${navigationLinks.TAX_PREACCOUNTING_LINK}/new`}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_PREACCOUNTING).build(),
                        <NewPreAccountingPage />
                    )}
                />
                <Route
                    path={`${navigationLinks.TAX_PREACCOUNTING_LINK}/:id`}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_PREACCOUNTING).build(),
                        <PreAccountingDetailPage />
                    )}
                />
                {/* --- Centre --- */}
                <Route
                    path={navigationLinks.TAX_CENTRES_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_CENTRE).build(), <CentrePage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CENTRES_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_CENTRE).build(), <NewCentrePage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CENTRES_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_CENTRE).build(), <CentreDetailPage />)}
                />
                {/* --- CLIENTS --- */}
                <Route
                    path={navigationLinks.TAX_CLIENTS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_CLIENT).build(), <ClientsPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CLIENTS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_CLIENT).build(), <NewClientPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CLIENTS_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_CLIENT).build(), <ClientDetailPage />)}
                />
                {/* --- LICENCES --- */}
                <Route
                    path={navigationLinks.TAX_LICENCES_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_LICENCE).build(), <LicencesPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_LICENCES_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_LICENCE).build(), <NewLicencePage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_LICENCES_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_LICENCE).build(), <LicenceDetailPage />)}
                />
                {/* --- OFFICERS --- */}
                <Route
                    path={navigationLinks.TAX_OFFICERS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_OFFICER).build(), <OfficersPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_OFFICERS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_OFFICER).build(), <NewOfficerPage />)}
                /> <Route
                    path={`${navigationLinks.TAX_OFFICERS_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_OFFICER).build(), <OfficersDetailPage />)}
                />
                {/* --- APPROVERS --- */}
                <Route
                    path={navigationLinks.TAX_APPROVERS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_APPROVER).build(), <ApproversPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_APPROVERS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_APPROVER).build(), <NewApproverPage />)}
                /> <Route
                    path={`${navigationLinks.TAX_APPROVERS_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_APPROVER).build(), <ApproversDetailPage />)}
                />
                {/* REVISIONS */}
                <Route path={`${navigationLinks.ADMIN_AUDIT}`} element={protectedRoute([SUPER_ADMIN_ROLE], <AuditPage />)} />
                <Route path={`${navigationLinks.ADMIN_AUDIT}/:id`} element={protectedRoute([SUPER_ADMIN_ROLE], <AuditDetailPage />)} />
                {/*PAYMENT REQUESTS */}
                <Route
                    path={navigationLinks.ADMIN_PAYMENT_REQUESTS_LINK}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PAYMENT_REQUEST).build(),
                        <PaymentRequestsPage />
                    )}
                />
                <Route
                    path={`${navigationLinks.ADMIN_PAYMENT_REQUESTS_LINK}/:id`}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PAYMENT_REQUEST).build(),
                        <PaymentRequestDetailPage />
                    )}
                />
                {/*PAYMENT TRANSACTIONS */}
                <Route
                    path={navigationLinks.ADMIN_PAYMENT_TRANSACTIONS_LINK}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <PaymentTransactionPage />)}
                />
                {/*PAYMENT ORDERS*/}
                <Route
                    path={navigationLinks.ADMIN_PAYMENT_ORDERS_LINK}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PAYMENT_ORDER).build(),
                        <PaymentOrdersPage />
                    )}
                />
                <Route
                    path={`${navigationLinks.ADMIN_PAYMENT_ORDERS_LINK}/:id`}
                    element={protectedRoute(
                        TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PAYMENT_ORDER).build(),
                        <PaymentOrderDetailPage />
                    )}
                />
                {/*PAYMENT OFFER*/}
                <Route path={navigationLinks.ADMIN_PAYMENT_OFFERS_LINK} element={protectedRoute([SUPER_ADMIN_ROLE], <PaymentOfferPage />)} />
                <Route
                    path={`${navigationLinks.ADMIN_PAYMENT_OFFERS_LINK}/new`}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <NewPaymentOfferPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_PAYMENT_OFFERS_LINK}/:id`}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <PaymentOfferDetailPage />)}
                />
                {/*PAYMENT COUPONS*/}
                <Route path={navigationLinks.ADMIN_PAYMENT_COUPONS_LINK} element={protectedRoute([SUPER_ADMIN_ROLE], <PaymentCouponPage />)} />
                <Route
                    path={`${navigationLinks.ADMIN_PAYMENT_COUPONS_LINK}/new`}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <NewPaymentCouponPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_PAYMENT_COUPONS_LINK}/:id`}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <PaymentCouponDetailPage />)}
                />
                {/*SUBSCRIPTIONS DASHBOARD*/}
                <Route
                    path={navigationLinks.ADMIN_SUBSCRIPTIONS_LINK}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <SubscriptionDashboardPage />)}
                />
                {/*CONTACTS*/}
                <Route
                    path={navigationLinks.TAX_CONTACTS_LINK}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_CONTACT).build(), <ContactPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CONTACTS_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(JmfPermissionEntity.CORE_CONTACT).build(), <NewContactPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CONTACTS_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_CONTACT).build(), <ContactDetailPage />)}
                />
                {/*Emails*/}
                <Route path={navigationLinks.ADMIN_EMAILS_LINK} element={protectedRoute([SUPER_ADMIN_ROLE], <EmailsPage />)} />
                <Route path={`${navigationLinks.ADMIN_EMAILS_LINK}/:id`} element={protectedRoute([SUPER_ADMIN_ROLE], <EmailDetailPage />)} />
                {/*REGISTRATION REQUESTS */}
                <Route
                    path={navigationLinks.ADMIN_REGISTRATION_REQUESTS_LINK}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <RegistrationRequestsPage />)}
                />
                <Route
                    path={`${navigationLinks.ADMIN_REGISTRATION_REQUESTS_LINK}/:id`}
                    element={protectedRoute([SUPER_ADMIN_ROLE], <RegistrationRequestDetailPage />)}
                />
                {/*VAT RATES*/}
                <Route path={`${navigationLinks.ADMIN_VAT_RATES_LINK}`} element={protectedRoute([SUPER_ADMIN_ROLE], <VatRatesPage />)} />
                <Route path={`${navigationLinks.ADMIN_VAT_RATES_LINK}/:id`} element={protectedRoute([SUPER_ADMIN_ROLE], <VatRatesDetailPage />)} />
                <Route path={`${navigationLinks.ADMIN_VAT_RATES_LINK}/new`} element={protectedRoute([SUPER_ADMIN_ROLE], <NewVatRatesPage />)} />
                {/*FEEDBACK*/}
                <Route path={navigationLinks.ADMIN_FEEDBACKS_LINK} element={protectedRoute([SUPER_ADMIN_ROLE], <FeedbackPage />)} />
                <Route path={navigationLinks.ADMIN_ONBOARDING_FEEDBACKS_LINK} element={protectedRoute([SUPER_ADMIN_ROLE], <OnboardingFeedbackPage />)} />
                {/*ABOUT*/}
                <Route path={navigationLinks.ABOUT_LINK} element={<AboutPage />} />
                {/*ACTIVITY*/}
                <Route
                    path={`${navigationLinks.TAX_ACTIVITY_LINK}`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_ACTIVITY).build(), <ActivitiesPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_ACTIVITY_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_ACTIVITY).build(), <NewActivityPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_ACTIVITY_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_ACTIVITY).build(), <ActivityDetailPage />)}
                />
                {/*CONTRACT*/}
                <Route
                    path={`${navigationLinks.TAX_CONTRACT_LINK}`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_CONTRACT).build(), <ContractPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CONTRACT_LINK}/:id`}
                    element={protectedRoute(TaxPermissionBuilder.builder().read(TaxPermissionEntity.TAX_CONTRACT).build(), <ContractDetailPage />)}
                />
                <Route
                    path={`${navigationLinks.TAX_CONTRACT_LINK}/new`}
                    element={protectedRoute(TaxPermissionBuilder.builder().create(TaxPermissionEntity.TAX_CONTRACT).build(), <NewContractPage />)}
                />
            </Route>
        </>
    )
);
